import React from "react";
import { graphql } from "gatsby";
import EmotionalLauncher, {
  EmotionalLauncherVariant
} from "components/EmotionalLauncher/EmotionalLauncher";
import { PageBuilderEmotionalLauncherFragment } from "types/graphql-types";
import EmotionalLauncherCruiseCount from "components/EmotionalLauncher/EmotionalLauncherCruiseCount/EmotionalLauncherCruiseCount";
import PageBuilderButtonCTA, {
  PageBuilderButtonCTAProps
} from "./PageBuilderButtonCTA";
import SubscribeToNewsletter from "components/SubscribeToNewsletter/SubscribeToNewsletter";
import { match } from "ts-pattern";
import { isPresent } from "src/utils/checks";
import { ButtonsContainer } from "@silversea-ssc/ui-components-react";
import { configToFYCQuery } from "components/PageBuilder/fyc/utils";
import { useFYC } from "components/FindYourCruise/useFYC";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { getPrivacyPolicyUrl } from "src/urls/subscribe_newsletter_urls";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";

type PageBuilderEmotionalLauncherProps = PageBuilderEmotionalLauncherFragment;

const PageBuilderEmotionalLauncher: React.FC<
  React.PropsWithChildren<PageBuilderEmotionalLauncherProps>
> = ({
  title,
  body,
  backgroundImage,
  ctaButtons,
  launcherType,
  variant,
  defaultContent,
  formDisclaimerContent,
  offersPagePath,
  ...props
}) => {
  const geolocation = useGeolocation();
  const cruiseCountQuery = useFYC(
    configToFYCQuery(props, geolocation.countryCode)
  );
  const language = useLanguage();

  return match(launcherType)
    .with("standard_cta", () => (
      <EmotionalLauncher
        title={title?.localized || ""}
        content={body?.localized}
        backgroundImage={backgroundImage}
        variant={variant as EmotionalLauncherVariant}
      >
        <CTAButtons ctaButtons={ctaButtons} />
      </EmotionalLauncher>
    ))
    .with("cruise_count", () => {
      const algoliaIndex = cruiseCountQuery.localizedSearchIndexName;
      if (!algoliaIndex) return null;

      return (
        <EmotionalLauncherCruiseCount
          title={title?.localized || ""}
          content={body?.localized}
          backgroundImage={backgroundImage}
          searchIndexName={algoliaIndex}
          searchQuery={cruiseCountQuery.filterDefaultSearch}
          variant={variant as EmotionalLauncherVariant}
        >
          <CTAButtons ctaButtons={ctaButtons} />
        </EmotionalLauncherCruiseCount>
      );
    })
    .with("subscribe_to_newsletter", () => (
      <SubscribeToNewsletter
        title={
          title?.localized || defaultContent?.heading?.title?.localized || ""
        }
        content={body?.localized || defaultContent?.heading?.content?.localized}
        backgroundImage={backgroundImage || defaultContent?.backgroundImage}
        formName="SubscribeToNewsletterSection"
        formDisclaimerContent={formDisclaimerContent}
        privacyPolicyUrl={getPrivacyPolicyUrl(language.code)}
        offerPageUrl={offersPagePath}
      />
    ))
    .otherwise(() => (
      <EmotionalLauncher
        title={title?.localized || ""}
        content={body?.localized}
        backgroundImage={backgroundImage}
        variant={variant as EmotionalLauncherVariant}
      />
    ));
};

export const CTAButtons: React.FC<
  {
    ctaButtons: PageBuilderEmotionalLauncherFragment["ctaButtons"];
  } & Pick<PageBuilderButtonCTAProps, "size">
> = ({ ctaButtons = [], ...props }) => (
  <ButtonsContainer
    align="left"
    mobileFullSize
    spacingTop="compact"
    gap="compact"
  >
    {ctaButtons.filter(isPresent).map((button) => (
      <PageBuilderButtonCTA
        key={button._key}
        {...button}
        {...props}
        iconName="arrow-right"
      />
    ))}
  </ButtonsContainer>
);

export default PageBuilderEmotionalLauncher;

// eslint-disable-next-line import/no-unused-modules
export const fragments = graphql`
  fragment PageBuilderEmotionalLauncher on SanityEmotionalLauncher {
    _key
    launcherType
    variant
    title {
      localized
    }
    body {
      localized
    }
    backgroundImage {
      ...Image
    }
    ctaButtons {
      ...ButtonCTA
    }
    filterByCruisesLaunch {
      cruiseCodes
    }
    filterByDestination {
      destinationWebCode
    }

    defaultContent {
      heading {
        title {
          localized
        }
        content {
          localized
        }
      }
      backgroundImage {
        ...ImageWithAlt
      }
    }

    formDisclaimerContent: formDisclaimer {
      ...FormDisclaimer
    }

    offersPagePath
  }
`;
